/**
 * Prompt Optimizer
 * 
 * WHAT NOBODY ELSE IS DOING:
 * - Automatically improves AI prompts to reduce hallucinations
 * - Adds context anchors and constraints
 * - Learns from successful/failed prompts
 * - Generates codebase-aware prompt enhancements
 */

import * as fs from 'fs';
import * as path from 'path';

export interface PromptAnalysis {
  original: string;
  issues: PromptIssue[];
  suggestions: PromptSuggestion[];
  score: number;
  optimized: string;
}

export interface PromptIssue {
  type: 'vague' | 'no_context' | 'no_constraints' | 'too_broad' | 'missing_examples' | 'ambiguous';
  severity: 'low' | 'medium' | 'high';
  description: string;
  location?: { start: number; end: number };
}

export interface PromptSuggestion {
  type: 'add_context' | 'add_constraint' | 'add_example' | 'be_specific' | 'add_format' | 'add_validation';
  priority: 'low' | 'medium' | 'high';
  suggestion: string;
  insertion?: string;
}

export interface OptimizationOptions {
  addCodebaseContext: boolean;
  addTypeConstraints: boolean;
  addExamples: boolean;
  addValidation: boolean;
  maxContextTokens: number;
  style: 'minimal' | 'detailed' | 'comprehensive';
}

export interface PromptTemplate {
  id: string;
  name: string;
  template: string;
  variables: string[];
  category: string;
  successRate: number;
  usageCount: number;
}

const DEFAULT_OPTIONS: OptimizationOptions = {
  addCodebaseContext: true,
  addTypeConstraints: true,
  addExamples: true,
  addValidation: true,
  maxContextTokens: 2000,
  style: 'detailed',
};

export class PromptOptimizer {
  private workspaceRoot: string;
  private templates: PromptTemplate[] = [];
  private promptHistory: Array<{ prompt: string; success: boolean; timestamp: string }> = [];
  private historyPath: string;

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
    this.historyPath = path.join(workspaceRoot, '.guardrail', 'prompt-history.json');
    this.loadHistory();
    this.initializeTemplates();
  }

  /**
   * Analyze and optimize a prompt
   */
  optimize(prompt: string, options: Partial<OptimizationOptions> = {}): PromptAnalysis {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    // Analyze the prompt
    const issues = this.analyzePrompt(prompt);
    const suggestions = this.generateSuggestions(prompt, issues, opts);
    
    // Calculate score
    const score = this.calculateScore(issues);
    
    // Generate optimized version
    const optimized = this.applyOptimizations(prompt, suggestions, opts);

    return {
      original: prompt,
      issues,
      suggestions,
      score,
      optimized,
    };
  }

  /**
   * Analyze prompt for issues
   */
  private analyzePrompt(prompt: string): PromptIssue[] {
    const issues: PromptIssue[] = [];
    const promptLower = prompt.toLowerCase();

    // Check for vague language
    const vaguePatterns = [
      { pattern: /make it better/i, desc: '"make it better" is vague - specify what to improve' },
      { pattern: /fix this/i, desc: '"fix this" without context - describe what\'s wrong' },
      { pattern: /something like/i, desc: '"something like" is imprecise - be specific' },
      { pattern: /do the thing/i, desc: 'Unclear reference - specify what "the thing" is' },
      { pattern: /you know what i mean/i, desc: 'Assumption of understanding - be explicit' },
      { pattern: /make it work/i, desc: '"make it work" is vague - define working behavior' },
      { pattern: /clean up/i, desc: '"clean up" is subjective - specify clean up criteria' },
      { pattern: /improve/i, desc: '"improve" without metrics - define improvement criteria' },
    ];

    for (const { pattern, desc } of vaguePatterns) {
      if (pattern.test(prompt)) {
        issues.push({
          type: 'vague',
          severity: 'medium',
          description: desc,
        });
      }
    }

    // Check for missing context
    const hasFileReference = /`[^`]+\.(ts|js|tsx|jsx|py|go|rs|java|cpp|c|h)`/.test(prompt);
    const hasCodeBlock = /```[\s\S]*```/.test(prompt);
    const hasFunctionName = /function\s+\w+|const\s+\w+\s*=|def\s+\w+|fn\s+\w+/.test(prompt);

    if (!hasFileReference && !hasCodeBlock && !hasFunctionName && prompt.length > 50) {
      issues.push({
        type: 'no_context',
        severity: 'high',
        description: 'No code context provided - add file references or code blocks',
      });
    }

    // Check for missing constraints
    const hasConstraints = /must|should|require|constraint|limit|only|don't|avoid|never|always/i.test(prompt);
    if (!hasConstraints && prompt.length > 100) {
      issues.push({
        type: 'no_constraints',
        severity: 'medium',
        description: 'No constraints specified - add requirements or limitations',
      });
    }

    // Check if too broad
    const broadPatterns = [
      /create an? (?:app|application|system|platform)/i,
      /build an? (?:complete|full|entire)/i,
      /implement everything/i,
      /do all/i,
    ];

    for (const pattern of broadPatterns) {
      if (pattern.test(prompt)) {
        issues.push({
          type: 'too_broad',
          severity: 'high',
          description: 'Request is too broad - break into smaller, specific tasks',
        });
        break;
      }
    }

    // Check for missing examples
    const wantsFormat = /format|output|return|response|result/i.test(prompt);
    const hasExample = /example:|for example|e\.g\.|such as:|like this:/i.test(prompt);
    if (wantsFormat && !hasExample) {
      issues.push({
        type: 'missing_examples',
        severity: 'low',
        description: 'Requesting specific format without providing examples',
      });
    }

    // Check for ambiguous pronouns
    const ambiguousPronouns = prompt.match(/\b(it|this|that|these|those)\b/gi) || [];
    if (ambiguousPronouns.length > 3) {
      issues.push({
        type: 'ambiguous',
        severity: 'low',
        description: 'Multiple ambiguous pronouns - be more specific about references',
      });
    }

    return issues;
  }

  /**
   * Generate optimization suggestions
   */
  private generateSuggestions(
    prompt: string,
    issues: PromptIssue[],
    options: OptimizationOptions
  ): PromptSuggestion[] {
    const suggestions: PromptSuggestion[] = [];

    // Suggest adding context
    if (issues.some(i => i.type === 'no_context') && options.addCodebaseContext) {
      suggestions.push({
        type: 'add_context',
        priority: 'high',
        suggestion: 'Add relevant file paths and code context',
        insertion: this.generateContextInsertion(),
      });
    }

    // Suggest adding constraints
    if (issues.some(i => i.type === 'no_constraints') && options.addTypeConstraints) {
      suggestions.push({
        type: 'add_constraint',
        priority: 'medium',
        suggestion: 'Add specific constraints and requirements',
        insertion: this.generateConstraintInsertion(prompt),
      });
    }

    // Suggest adding examples
    if (issues.some(i => i.type === 'missing_examples') && options.addExamples) {
      suggestions.push({
        type: 'add_example',
        priority: 'medium',
        suggestion: 'Add example of expected output format',
        insertion: '\n\nExample output format:\n```\n// Your example here\n```',
      });
    }

    // Suggest being more specific
    if (issues.some(i => i.type === 'vague')) {
      suggestions.push({
        type: 'be_specific',
        priority: 'high',
        suggestion: 'Replace vague language with specific requirements',
      });
    }

    // Suggest breaking down broad requests
    if (issues.some(i => i.type === 'too_broad')) {
      suggestions.push({
        type: 'be_specific',
        priority: 'high',
        suggestion: 'Break this into smaller, focused tasks',
        insertion: '\n\nLet\'s start with step 1: [specific first task]',
      });
    }

    // Add validation suggestion
    if (options.addValidation) {
      suggestions.push({
        type: 'add_validation',
        priority: 'low',
        suggestion: 'Add validation criteria for the output',
        insertion: '\n\nValidation criteria:\n- [ ] Code compiles without errors\n- [ ] Types are correct\n- [ ] Follows existing patterns',
      });
    }

    return suggestions;
  }

  /**
   * Apply optimizations to prompt
   */
  private applyOptimizations(
    prompt: string,
    suggestions: PromptSuggestion[],
    options: OptimizationOptions
  ): string {
    let optimized = prompt;

    // Apply insertions from high-priority suggestions
    const highPriority = suggestions.filter(s => s.priority === 'high' && s.insertion);
    for (const suggestion of highPriority) {
      if (suggestion.insertion) {
        optimized = this.insertSuggestion(optimized, suggestion);
      }
    }

    // Add codebase context header if needed
    if (options.addCodebaseContext && !optimized.includes('Context:')) {
      const contextHeader = this.generateContextHeader(options.style);
      optimized = contextHeader + '\n\n' + optimized;
    }

    // Add constraints footer if needed
    if (options.addTypeConstraints && !optimized.includes('Constraints:')) {
      const constraintsFooter = this.generateConstraintsFooter(options.style);
      optimized = optimized + '\n\n' + constraintsFooter;
    }

    // Add validation section if requested
    if (options.addValidation && !optimized.includes('Validation:')) {
      optimized += '\n\n' + this.generateValidationSection();
    }

    return optimized;
  }

  /**
   * Insert suggestion into prompt
   */
  private insertSuggestion(prompt: string, suggestion: PromptSuggestion): string {
    if (!suggestion.insertion) return prompt;

    switch (suggestion.type) {
      case 'add_context':
        return suggestion.insertion + '\n\n' + prompt;
      case 'add_constraint':
      case 'add_validation':
      case 'add_example':
        return prompt + suggestion.insertion;
      default:
        return prompt + '\n' + suggestion.insertion;
    }
  }

  /**
   * Generate context insertion
   */
  private generateContextInsertion(): string {
    return `**Context:**
- Working in: \`${path.basename(this.workspaceRoot)}\`
- Refer to existing code patterns in the codebase
- Use imports from existing modules where applicable`;
  }

  /**
   * Generate constraint insertion
   */
  private generateConstraintInsertion(prompt: string): string {
    const constraints: string[] = [];
    
    // Detect what kind of code is being requested
    if (/function|method|handler/i.test(prompt)) {
      constraints.push('- Function should be pure where possible');
      constraints.push('- Add proper TypeScript types');
      constraints.push('- Handle edge cases and errors');
    }
    
    if (/component|ui|view/i.test(prompt)) {
      constraints.push('- Use existing component patterns');
      constraints.push('- Add proper prop types');
      constraints.push('- Handle loading and error states');
    }

    if (/api|endpoint|route/i.test(prompt)) {
      constraints.push('- Follow REST conventions');
      constraints.push('- Add input validation');
      constraints.push('- Return appropriate status codes');
    }

    if (constraints.length === 0) {
      constraints.push('- Follow existing code style');
      constraints.push('- Add appropriate error handling');
      constraints.push('- Use TypeScript types');
    }

    return '\n\n**Constraints:**\n' + constraints.join('\n');
  }

  /**
   * Generate context header based on style
   */
  private generateContextHeader(style: OptimizationOptions['style']): string {
    if (style === 'minimal') {
      return `**Project:** ${path.basename(this.workspaceRoot)}`;
    }

    if (style === 'detailed') {
      return `**Context:**
- Project: ${path.basename(this.workspaceRoot)}
- Use existing patterns and imports from the codebase
- Follow the established code style`;
    }

    return `**Context:**
- Project: ${path.basename(this.workspaceRoot)}
- Language: TypeScript
- Use existing patterns and imports from the codebase
- Follow the established code style and conventions
- Ensure code integrates with existing architecture
- Reference existing types and interfaces where applicable`;
  }

  /**
   * Generate constraints footer based on style
   */
  private generateConstraintsFooter(style: OptimizationOptions['style']): string {
    if (style === 'minimal') {
      return `**Requirements:** Use TypeScript, handle errors`;
    }

    if (style === 'detailed') {
      return `**Requirements:**
- Use TypeScript with proper types
- Handle errors appropriately
- Follow existing patterns in the codebase
- Add comments for complex logic`;
    }

    return `**Requirements:**
- Use TypeScript with strict types (no \`any\`)
- Handle errors with try/catch or Result types
- Follow existing patterns in the codebase
- Add JSDoc comments for public APIs
- Ensure backward compatibility
- Consider edge cases
- Write testable code`;
  }

  /**
   * Generate validation section
   */
  private generateValidationSection(): string {
    return `**Before completing, verify:**
- [ ] Code compiles without TypeScript errors
- [ ] All imports exist and are correct
- [ ] Types match existing definitions
- [ ] Error handling is in place
- [ ] Follows existing code patterns`;
  }

  /**
   * Calculate prompt quality score
   */
  private calculateScore(issues: PromptIssue[]): number {
    let score = 100;
    
    for (const issue of issues) {
      switch (issue.severity) {
        case 'high':
          score -= 20;
          break;
        case 'medium':
          score -= 10;
          break;
        case 'low':
          score -= 5;
          break;
      }
    }

    return Math.max(0, score);
  }

  /**
   * Record prompt result for learning
   */
  recordResult(prompt: string, success: boolean): void {
    this.promptHistory.push({
      prompt,
      success,
      timestamp: new Date().toISOString(),
    });
    
    // Keep only last 500 prompts
    if (this.promptHistory.length > 500) {
      this.promptHistory = this.promptHistory.slice(-500);
    }
    
    this.saveHistory();
  }

  /**
   * Get prompt templates
   */
  getTemplates(category?: string): PromptTemplate[] {
    if (category) {
      return this.templates.filter(t => t.category === category);
    }
    return this.templates;
  }

  /**
   * Apply a template
   */
  applyTemplate(templateId: string, variables: Record<string, string>): string {
    const template = this.templates.find(t => t.id === templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }

    let result = template.template;
    for (const [key, value] of Object.entries(variables)) {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    }

    return result;
  }

  /**
   * Get prompt improvement suggestions based on history
   */
  getHistoricalInsights(): {
    successRate: number;
    commonIssues: string[];
    recommendations: string[];
  } {
    if (this.promptHistory.length === 0) {
      return {
        successRate: 0,
        commonIssues: [],
        recommendations: ['Start using prompt tracking to get insights'],
      };
    }

    const successRate = this.promptHistory.filter(p => p.success).length / this.promptHistory.length;

    // Analyze failed prompts
    const failedPrompts = this.promptHistory.filter(p => !p.success).map(p => p.prompt);
    const commonIssues: string[] = [];

    let vagueCount = 0;
    let noContextCount = 0;
    
    for (const prompt of failedPrompts) {
      if (/make it|fix this|something like/i.test(prompt)) vagueCount++;
      if (!(/`[^`]+\.(ts|js)`/.test(prompt) || /```/.test(prompt))) noContextCount++;
    }

    if (vagueCount > failedPrompts.length * 0.3) {
      commonIssues.push('Vague language in prompts');
    }
    if (noContextCount > failedPrompts.length * 0.5) {
      commonIssues.push('Missing code context');
    }

    const recommendations: string[] = [];
    if (successRate < 0.5) {
      recommendations.push('Consider adding more specific context to prompts');
    }
    if (commonIssues.includes('Vague language in prompts')) {
      recommendations.push('Use specific, actionable language');
    }
    if (commonIssues.includes('Missing code context')) {
      recommendations.push('Include relevant code snippets or file references');
    }

    return {
      successRate: Math.round(successRate * 100),
      commonIssues,
      recommendations,
    };
  }

  /**
   * Initialize built-in templates
   */
  private initializeTemplates(): void {
    this.templates = [
      {
        id: 'create-function',
        name: 'Create Function',
        template: `Create a TypeScript function named \`{{functionName}}\` that {{description}}.

**Requirements:**
- Input: {{input}}
- Output: {{output}}
- Handle errors appropriately
- Add JSDoc comments

**Context:**
{{context}}`,
        variables: ['functionName', 'description', 'input', 'output', 'context'],
        category: 'code',
        successRate: 0.85,
        usageCount: 0,
      },
      {
        id: 'fix-bug',
        name: 'Fix Bug',
        template: `Fix the following bug in \`{{file}}\`:

**Current behavior:** {{currentBehavior}}
**Expected behavior:** {{expectedBehavior}}

**Relevant code:**
\`\`\`typescript
{{code}}
\`\`\`

**Constraints:**
- Don't change the function signature
- Maintain backward compatibility
- Add a comment explaining the fix`,
        variables: ['file', 'currentBehavior', 'expectedBehavior', 'code'],
        category: 'debugging',
        successRate: 0.80,
        usageCount: 0,
      },
      {
        id: 'add-feature',
        name: 'Add Feature',
        template: `Add a new feature to {{component}}:

**Feature:** {{featureDescription}}

**Acceptance criteria:**
{{acceptanceCriteria}}

**Existing code to modify:**
\`\`\`typescript
{{existingCode}}
\`\`\`

**Requirements:**
- Follow existing patterns
- Add tests
- Update types as needed`,
        variables: ['component', 'featureDescription', 'acceptanceCriteria', 'existingCode'],
        category: 'feature',
        successRate: 0.75,
        usageCount: 0,
      },
      {
        id: 'refactor',
        name: 'Refactor Code',
        template: `Refactor the following code in \`{{file}}\`:

**Current code:**
\`\`\`typescript
{{currentCode}}
\`\`\`

**Refactoring goals:**
{{goals}}

**Constraints:**
- Maintain the same external API
- Improve readability and maintainability
- Add types if missing`,
        variables: ['file', 'currentCode', 'goals'],
        category: 'refactoring',
        successRate: 0.82,
        usageCount: 0,
      },
      {
        id: 'write-test',
        name: 'Write Tests',
        template: `Write tests for \`{{functionName}}\` in \`{{file}}\`:

**Function to test:**
\`\`\`typescript
{{code}}
\`\`\`

**Test cases to cover:**
{{testCases}}

**Requirements:**
- Use {{testFramework}}
- Test edge cases
- Mock external dependencies`,
        variables: ['functionName', 'file', 'code', 'testCases', 'testFramework'],
        category: 'testing',
        successRate: 0.78,
        usageCount: 0,
      },
    ];
  }

  private loadHistory(): void {
    try {
      if (fs.existsSync(this.historyPath)) {
        const data = JSON.parse(fs.readFileSync(this.historyPath, 'utf-8'));
        this.promptHistory = data.history || [];
      }
    } catch (e) {
      console.error('Failed to load prompt history:', e);
    }
  }

  private saveHistory(): void {
    try {
      const dir = path.dirname(this.historyPath);
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true });
      }
      fs.writeFileSync(this.historyPath, JSON.stringify({
        history: this.promptHistory,
        lastUpdated: new Date().toISOString(),
      }, null, 2));
    } catch (e) {
      console.error('Failed to save prompt history:', e);
    }
  }
}

export function createPromptOptimizer(workspaceRoot: string): PromptOptimizer {
  return new PromptOptimizer(workspaceRoot);
}
